import React, { useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import { Helmet } from '@dr.pogodin/react-helmet'
import useFetchEntitiesWithCache from '../../hooks/useFetchEntitiesWithCache'
import { useAtom } from 'jotai'

import { Box, Divider as MuiDivider, Typography } from '@mui/material'
import { spacing } from '@mui/system'
import { useTranslation } from 'react-i18next'

import CompaniesGrid from '../../components/entities/CompaniesGrid'
import LoadingIndicator from '../../components/LoadingIndicator'
import { useLogUserEvent } from '../../hooks/useLogUserEvent'
import { CompaniesFilters, getSectorId } from './CompaniesFilters'
import { companyFiltersAtom } from '../../atoms/companyAtom'
import { CompanyEntity } from '../../types/CompanyEntity'
import { useWatchListFilter } from '../../hooks/useWatchListFilter'

const Divider = styled(MuiDivider)(spacing)

function CompaniesView() {
    const { t } = useTranslation()
    const [filteredCompanies, setFilteredCompanies] = useState<CompanyEntity[]>([])
    const { isLoading, result: companies } = useFetchEntitiesWithCache('company')
    const [filters] = useAtom(companyFiltersAtom)
    void useLogUserEvent('company')
    const { watchListFilter } = useWatchListFilter(filters.onlyInWatchLists.value)

    useEffect(() => {
        if (!companies?.length) return

        const filtered = companies.filter((company) => {
            const mcap = !company.isDelisted && company.marketcap !== null ? company.marketcap / 1000000 : 0

            return (
                watchListFilter(company) &&
                (filters.sectors.value.length === 0 ||
                    (![company.sector1, company.sector2].includes(null) &&
                        filters.sectors.value.includes(getSectorId(company.sector1 as string, company.sector2)))) &&
                (filters.marketplaces.value.length === 0 ||
                    filters.marketplaces.value.includes(company.marketplaceId)) &&
                (filters.listed.value.includes('all') ||
                    (filters.listed.value.includes('only-listed') ? !company.isDelisted : company.isDelisted)) &&
                mcap >= filters.marketSize.value[0] &&
                (filters.marketSize.hasMaxValueSet || mcap <= filters.marketSize.value[1])
            )
        })

        setFilteredCompanies(filtered)
    }, [filters, companies, watchListFilter])

    return (
        <React.Fragment>
            <Helmet title={t('entities.company.plural')} />
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'inline',
                }}
            >
                {t('entities.company.plural')}
            </Typography>
            <Divider my={6} />
            <Box>
                <LoadingIndicator
                    loading={isLoading}
                    label={t('site.loading.companies')}
                    labelSx={{
                        color: 'primary.main',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        textAlign: 'center',
                    }}
                />
                {!isLoading && <CompaniesFilters companies={companies || []} />}
                {!isLoading && <CompaniesGrid companies={filteredCompanies} />}
            </Box>
        </React.Fragment>
    )
}

export default CompaniesView
